type Route = { path: string; fullPath?: string; name?: string | symbol | null };
type Phase =
    | 'entry_evaluated'
    | 'plugins_registered'
    | 'app_mounted'
    | 'route_resolved'
    | 'admission'
    | 'auth_accounts'
    | 'auth_profile_abilities_previews'
    | 'account_list'
    | 'configs'
    | 'features'
    | 'configs_features'
    | 'context'
    | 'groups'
    | 'shell_ready'
    | 'lead_content_render_opportunity'
    | 'all_lead_cards_data_ready'
    | 'all_lead_table_data_ready';
export type StartupScope = { path: string };

const MAX_DURATION_MS = 60_000;

const entryType = (path: string): string => {
    if (/^\/leads\/\d+\/?$/.test(path)) return 'direct_lead';
    if (/^\/dashboards\/all-lead\/?$/.test(path)) return 'direct_all_lead';
    if (path === '/') return 'root';
    if (path === '/auth/callback') return 'auth_callback';
    if (path === '/auth/mfa-callback') return 'mfa_callback';
    return 'other';
};

/** App-specific readiness guards; New Relic collects the native measurements. */
export function createStartupTiming({
    path,
    timeline = performance,
    now = () => performance.now(),
    frame = (callback: FrameRequestCallback) => requestAnimationFrame(callback),
    visible = () => document.visibilityState === 'visible',
}: {
    path: string;
    timeline?: Pick<Performance, 'mark' | 'measure'>;
    now?: () => number;
    frame?: (callback: FrameRequestCallback) => unknown;
    visible?: () => boolean;
}) {
    const entry = entryType(path);
    const phases = new Set<Phase>();
    const spans = new Set<Phase>();
    let active = entry !== 'other';
    let target: StartupScope | null = null;
    let targetKey: string | null = null;
    let shellReady = false;
    let entryShellReady = false;
    let pending: { scope: StartupScope; ready: () => boolean } | null = null;

    const current = (scope?: StartupScope | null): boolean =>
        active && now() < MAX_DURATION_MS && (!scope || target === scope);

    const name = (phase: Phase): string => `activix.startup.${entry}.${phase}`;
    const measure = (phase: Phase, startedAt: number, status: 'resolved' | 'rejected'): void => {
        try {
            if (current())
                timeline.measure(`${name(phase)}.${status}`, { start: startedAt, end: now() });
        } catch {
            // Performance APIs must never interrupt the measured action.
        }
    };
    const mark = (phase: Phase): void => {
        try {
            if (!current() || phases.has(phase)) return;
            phases.add(phase);
            timeline.mark(name(phase));
            timeline.measure(name(phase), { start: 0, end: now() });
        } catch {
            // Telemetry must not interrupt application startup.
        }
    };

    const flushReady = (): void => {
        const candidate = pending;
        if (!candidate || !shellReady || !current(candidate.scope)) return;
        try {
            frame(() => {
                frame(() => {
                    try {
                        if (
                            pending === candidate &&
                            current(candidate.scope) &&
                            shellReady &&
                            visible() &&
                            candidate.ready()
                        ) {
                            mark('lead_content_render_opportunity');
                            pending = null;
                        }
                    } catch {
                        // A stale or unmounted view is not readiness evidence.
                    }
                });
            });
        } catch {
            // Unsupported frame scheduling must not affect rendering.
        }
    };

    return {
        mark,
        observeRoute(route: Route): void {
            if (!current()) return;
            if (target) {
                if (
                    target.path !== route.path ||
                    (entryType(target.path) !== 'direct_all_lead' &&
                        targetKey !== (route.fullPath ?? route.path))
                ) {
                    active = false;
                    pending = null;
                } else {
                    targetKey = route.fullPath ?? route.path;
                }
                return;
            }
            const type = entryType(route.path);
            if (type === 'direct_lead' || type === 'direct_all_lead') {
                target = { path: route.path };
                targetKey = route.fullPath ?? route.path;
                if (entryShellReady) {
                    shellReady = true;
                    mark('shell_ready');
                }
            } else if (!['root', 'auth_callback', 'mfa_callback'].includes(type)) {
                active = false;
            }
        },
        capture(route: Route): StartupScope | null {
            return current(target) && targetKey === (route.fullPath ?? route.path) ? target : null;
        },
        markData(
            phase: 'all_lead_cards_data_ready' | 'all_lead_table_data_ready',
            scope: StartupScope | null
        ): void {
            if (scope && current(scope) && entryType(scope.path) === 'direct_all_lead') mark(phase);
        },
        shell(ready: boolean, route: Route): void {
            if (
                !target &&
                ['root', 'auth_callback', 'mfa_callback'].includes(entryType(route.path))
            ) {
                entryShellReady = ready;
            }
            shellReady = Boolean(ready && target?.path === route.path);
            if (shellReady) {
                mark('shell_ready');
                flushReady();
            }
        },
        leadReady(scope: StartupScope | null, ready: () => boolean): void {
            if (!scope || !current(scope) || entryType(scope.path) !== 'direct_lead') return;
            pending = { scope, ready };
            flushReady();
        },
        async measure<T>(phase: Phase, action: () => Promise<T>): Promise<T> {
            const record = current() && !spans.has(phase);
            const startedAt = now();
            if (record) spans.add(phase);
            try {
                const result = await action();
                if (record) measure(phase, startedAt, 'resolved');
                return result;
            } catch (error) {
                if (record) measure(phase, startedAt, 'rejected');
                throw error;
            }
        },
        stop(): void {
            active = false;
            pending = null;
        },
    };
}

let startup: ReturnType<typeof createStartupTiming> | null = null;

export function initializeStartupTiming(): void {
    try {
        if (entryType(globalThis.location.pathname) === 'other') return;
        startup = createStartupTiming({ path: globalThis.location.pathname });
        startup.mark('entry_evaluated');
        globalThis.addEventListener('pagehide', () => startup?.stop(), { once: true });
    } catch {
        startup = null;
    }
}

export const markStartupPhase = (phase: Phase): void => startup?.mark(phase);
export const observeStartupRoute = (route: Route): void => startup?.observeRoute(route);
export const captureStartupScope = (route: Route): StartupScope | null =>
    startup?.capture(route) ?? null;
export const observeStartupShell = (ready: boolean, route: Route): void =>
    startup?.shell(ready, route);
export const observeStartupLeadReady = (scope: StartupScope | null, ready: () => boolean): void =>
    startup?.leadReady(scope, ready);
export const markStartupData = (
    phase: 'all_lead_cards_data_ready' | 'all_lead_table_data_ready',
    scope: StartupScope | null
): void => startup?.markData(phase, scope);
export const measureStartupPhase = <T>(phase: Phase, action: () => Promise<T>): Promise<T> =>
    startup ? startup.measure(phase, action) : action();
